/**
 * Trash Manager
 * 
 * Handles hiding/showing sections and panels in the trash
 */

import { settings } from './Settings.js';

export class TrashManager {
    constructor(apiBase = '/api/user/') {
        this.apiBase = apiBase;
        this.trashedItems = [];
        this.isLoggedIn = false;
        this.userId = null;
        this.trashModal = null;
        
        this.init();
    }
    
    async init() {
        await this.checkLoginStatus();
        await this.loadTrashedItems();
        this.createTrashModal();
        this.setupTrashDropZone();
    }
    
    /**
     * Check if user is logged in
     */
    async checkLoginStatus() {
        try {
            const response = await fetch(`${this.apiBase.replace('/user/', '/auth/')}check.php`);
            if (response.ok) {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const data = await response.json();
                    this.isLoggedIn = data.logged_in || false;
                    this.userId = data.user_id || null;
                } else {
                    // Response is not JSON (likely HTML 404 page) - old PHP endpoint doesn't exist
                    this.isLoggedIn = false;
                }
            }
        } catch (e) {
            // Silently fail - auth check is optional for planner mode
            this.isLoggedIn = false;
        }
    }
    
    /**
     * Create trash modal UI
     */
    createTrashModal() {
        // Remove existing modal if present
        const existing = document.getElementById('trash-modal');
        if (existing) {
            existing.remove();
        }
        
        const modal = document.createElement('div');
        modal.id = 'trash-modal';
        modal.className = 'trash-modal';
        modal.innerHTML = `
            <div class="trash-modal-content">
                <div class="trash-modal-header">
                    <h3>Trash</h3>
                    <button class="trash-modal-close" id="trash-modal-close" aria-label="Close">×</button>
                </div>
                <div class="trash-modal-body">
                    <p class="trash-empty-message" id="trash-empty-message" style="display: none;">
                        Trash is empty. Drag sections or panels here to hide them.
                    </p>
                    <div class="trash-items-list" id="trash-items-list"></div>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
        this.trashModal = modal;
        
        // Close button
        const closeBtn = document.getElementById('trash-modal-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => {
                this.hideModal();
            });
        }
        
        // Close on outside click
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                this.hideModal();
            }
        });
        
        // Update display
        this.updateTrashDisplay();
    }
    
    /**
     * Show trash modal
     */
    showModal() {
        if (this.trashModal) {
            this.trashModal.classList.add('show');
            this.updateTrashDisplay();
        }
    }
    
    /**
     * Hide trash modal
     */
    hideModal() {
        if (this.trashModal) {
            this.trashModal.classList.remove('show');
        }
    }
    
    /**
     * Toggle trash modal
     */
    toggleModal() {
        if (this.trashModal && this.trashModal.classList.contains('show')) {
            this.hideModal();
        } else {
            this.showModal();
        }
    }
    
    /**
     * Update trash items display
     */
    updateTrashDisplay() {
        const list = document.getElementById('trash-items-list');
        const emptyMsg = document.getElementById('trash-empty-message');
        
        if (!list) return;
        
        if (this.trashedItems.length === 0) {
            if (emptyMsg) emptyMsg.style.display = 'block';
            list.innerHTML = '';
            return;
        }
        
        if (emptyMsg) emptyMsg.style.display = 'none';
        
        list.innerHTML = this.trashedItems.map((item, index) => {
            const type = item.isPanel ? 'Panel' : 'Section';
            const name = item.name || item.sectionId || item.panelId || 'Unknown';
            return `
                <div class="trash-item" data-index="${index}">
                    <div class="trash-item-info">
                        <span class="trash-item-type">${type}</span>
                        <span class="trash-item-name">${name}</span>
                    </div>
                    <button class="trash-item-restore" data-index="${index}" title="Restore">↩️ Restore</button>
                </div>
            `;
        }).join('');
        
        // Add restore button listeners
        list.querySelectorAll('.trash-item-restore').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const index = parseInt(e.target.dataset.index);
                this.restoreItem(index);
            });
        });
    }
    
    /**
     * Setup trash drop zone
     */
    setupTrashDropZone() {
        const trashBtn = document.getElementById('trashBtn');
        if (!trashBtn) return;
        
        trashBtn.addEventListener('dragover', (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            trashBtn.classList.add('trash-drag-over');
        });
        
        trashBtn.addEventListener('dragleave', (e) => {
            if (!trashBtn.contains(e.relatedTarget)) {
                trashBtn.classList.remove('trash-drag-over');
            }
        });
        
        trashBtn.addEventListener('drop', (e) => {
            e.preventDefault();
            trashBtn.classList.remove('trash-drag-over');
            
            // Get dragged element from CrossContainerDragDrop or SectionDragDrop
            const sectionId = e.dataTransfer.getData('text/plain') || 
                             e.dataTransfer.getData('section-id') || 
                             e.dataTransfer.getData('text/html');
            
            // Try to find the element
            let element = null;
            
            // Check for section
            if (sectionId) {
                element = document.querySelector(`[data-section-id="${sectionId}"]`);
            }
            
            // Check for panel
            if (!element) {
                const panelId = e.dataTransfer.getData('text/plain');
                if (panelId) {
                    element = document.querySelector(`[data-panel-id="${panelId}"]`);
                }
            }
            
            // Fallback: try to find by class
            if (!element) {
                // This might be set by the drag handlers
                const draggedElements = document.querySelectorAll('.dragging');
                if (draggedElements.length > 0) {
                    element = draggedElements[0];
                }
            }
            
            if (element) {
                this.trashItem(element);
            }
        });
    }
    
    /**
     * Trash an item (section or panel)
     */
    trashItem(element) {
        if (!element) return;
        
        const isPanel = element.hasAttribute('data-panel-id');
        const sectionId = element.dataset.sectionId;
        const panelId = element.dataset.panelId;
        
        // Get name
        let name = '';
        if (isPanel) {
            // Try to get panel name from class or id
            const panelClass = element.className;
            if (panelClass.includes('left-panel')) name = 'Left Panel';
            else if (panelClass.includes('right-panel')) name = 'Right Panel';
            else if (panelClass.includes('canvas-area')) name = 'Canvas Area';
            else name = panelId || 'Panel';
        } else {
            // Try to get section name from header
            const header = element.querySelector('h3, h4');
            if (header) {
                name = header.textContent.trim();
            } else {
                name = sectionId || 'Section';
            }
        }
        
        // Store original container info
        let originalContainer = null;
        const containers = [
            { selector: '#left-panel-sections', id: 'left-panel' },
            { selector: '#right-panel-sections', id: 'right-panel' },
            { selector: '.tabs-below-canvas', id: 'tabs-below-canvas' },
            { selector: '#resources-panel', id: 'resources-panel' },
            { selector: '#military-panel', id: 'military-panel' }
        ];
        
        for (const container of containers) {
            const containerEl = document.querySelector(container.selector);
            if (containerEl && containerEl.contains(element)) {
                originalContainer = container.id;
                break;
            }
        }
        
        // If no container found, check if it's a panel
        if (!originalContainer && isPanel) {
            originalContainer = 'main-container-panels';
        }
        
        // Create trash item record
        const trashItem = {
            isPanel: isPanel,
            sectionId: sectionId,
            panelId: panelId,
            name: name,
            originalContainer: originalContainer,
            html: element.outerHTML,
            index: this.trashedItems.length
        };
        
        // Hide element
        element.style.display = 'none';
        element.setAttribute('data-trashed', 'true');
        
        // Add to trash
        this.trashedItems.push(trashItem);
        
        // Save and update display
        this.saveTrashedItems();
        this.updateTrashDisplay();
        
        // Update trash button badge
        this.updateTrashBadge();
    }
    
    /**
     * Restore an item from trash
     */
    restoreItem(index) {
        if (index < 0 || index >= this.trashedItems.length) return;
        
        const item = this.trashedItems[index];
        
        // Find or recreate the element
        let element = null;
        
        if (item.isPanel) {
            element = document.querySelector(`[data-panel-id="${item.panelId}"][data-trashed="true"]`);
        } else {
            element = document.querySelector(`[data-section-id="${item.sectionId}"][data-trashed="true"]`);
        }
        
        // If element exists, restore it
        if (element) {
            element.style.display = '';
            element.removeAttribute('data-trashed');
            
            // Try to restore to original container
            if (item.originalContainer) {
                const containers = [
                    { selector: '#left-panel-sections', id: 'left-panel' },
                    { selector: '#right-panel-sections', id: 'right-panel' },
                    { selector: '.tabs-below-canvas', id: 'tabs-below-canvas' },
                    { selector: '#resources-panel', id: 'resources-panel' },
                    { selector: '#military-panel', id: 'military-panel' }
                ];
                
                if (item.originalContainer === 'main-container-panels') {
                    const mainContainer = document.getElementById('main-container-panels');
                    if (mainContainer && !mainContainer.contains(element)) {
                        mainContainer.appendChild(element);
                    }
                } else {
                    const container = containers.find(c => c.id === item.originalContainer);
                    if (container) {
                        const containerEl = document.querySelector(container.selector);
                        if (containerEl && !containerEl.contains(element)) {
                            containerEl.appendChild(element);
                        }
                    }
                }
            }
        }
        
        // Remove from trash
        this.trashedItems.splice(index, 1);
        
        // Save and update
        this.saveTrashedItems();
        this.updateTrashDisplay();
        this.updateTrashBadge();
        
        // Refresh drag and drop if needed
        if (window.mainApp && window.mainApp.crossContainerDragDrop) {
            window.mainApp.crossContainerDragDrop.refreshDragAndDrop();
        }
    }
    
    /**
     * Update trash button badge
     */
    updateTrashBadge() {
        const trashBtn = document.getElementById('trashBtn');
        if (!trashBtn) return;
        
        const count = this.trashedItems.length;
        let badge = trashBtn.querySelector('.trash-badge');
        
        if (count > 0) {
            if (!badge) {
                badge = document.createElement('span');
                badge.className = 'trash-badge';
                trashBtn.appendChild(badge);
            }
            badge.textContent = count;
        } else {
            if (badge) {
                badge.remove();
            }
        }
    }
    
    /**
     * Load trashed items from storage
     */
    async loadTrashedItems() {
        let savedItems = null;
        
        // Try to load from database first (if logged in)
        if (this.isLoggedIn) {
            try {
                const response = await fetch(`${this.apiBase}preferences.php`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.preferences && data.preferences.trashedItems) {
                        savedItems = data.preferences.trashedItems;
                        // Sync to localStorage for offline access
                        settings.set('trashedItems', savedItems);
                    }
                }
            } catch (e) {
                console.log('Failed to load trashed items from database:', e);
            }
        }
        
        // Fallback to localStorage
        if (!savedItems) {
            const stored = settings.get('trashedItems');
            if (stored && Array.isArray(stored)) {
                savedItems = stored;
            }
        }
        
        if (savedItems) {
            this.trashedItems = savedItems;
            this.applyTrashedItems();
        }
    }
    
    /**
     * Apply trashed items (hide them on load)
     */
    applyTrashedItems() {
        this.trashedItems.forEach(item => {
            let element = null;
            
            if (item.isPanel) {
                element = document.querySelector(`[data-panel-id="${item.panelId}"]`);
            } else {
                element = document.querySelector(`[data-section-id="${item.sectionId}"]`);
            }
            
            if (element) {
                element.style.display = 'none';
                element.setAttribute('data-trashed', 'true');
            }
        });
        
        this.updateTrashBadge();
    }
    
    /**
     * Save trashed items to storage
     */
    async saveTrashedItems() {
        // Save to localStorage immediately
        settings.set('trashedItems', this.trashedItems);
        
        // Save to database if logged in
        if (this.isLoggedIn) {
            try {
                // Load existing preferences first to merge
                let existingPrefs = {};
                try {
                    const getResponse = await fetch(`${this.apiBase}preferences.php`);
                    if (getResponse.ok) {
                        const getData = await getResponse.json();
                        if (getData.success && getData.preferences) {
                            existingPrefs = getData.preferences;
                        }
                    }
                } catch (e) {
                    // Ignore errors loading existing prefs
                }
                
                // Merge with new trashed items
                const preferences = {
                    ...existingPrefs,
                    trashedItems: this.trashedItems
                };
                
                const response = await fetch(`${this.apiBase}preferences.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ preferences })
                });
                
                if (!response.ok) {
                    console.warn('Failed to save trashed items to database');
                }
            } catch (e) {
                console.warn('Error saving trashed items to database:', e);
            }
        }
    }
}

